/**
 * P2.2: Procesa la cola de mutaciones al recuperar red.
 * Llama a OrderService por cada ítem; invalida queries al terminar.
 */
import type { QueryClient } from '@tanstack/react-query';
import { OrderService, bustDashboardStatsCache } from '../services/orderService';
import { queryKeys } from '@/hooks/queries/queryKeys';
import type { QueuedMutation } from './queue';
import { getPendingMutations, removeMutation, updateMutation, quarantineMutation } from './queue';
import type { WorkOrder } from '@/types';

export type ProcessQueueResult = { processed: number; failed: number };

const MAX_MUTATION_RETRIES = 3;

export async function processQueue(queryClient: QueryClient): Promise<ProcessQueueResult> {
    const pending = await getPendingMutations();
    let processed = 0;
    let failed = 0;

    for (const item of pending) {
        try {
            await runMutation(item);
            await removeMutation(item.id);
            processed++;
        } catch (err: any) {
            const currentRetries = (item.retries || 0) + 1;
            const errorMessage = err?.message || 'Error desconocido';
            console.error(`[OfflineSync] Failed to apply mutation (${item.type}, id: ${item.id}, intento ${currentRetries}/${MAX_MUTATION_RETRIES}):`, err);
            
            if (currentRetries >= MAX_MUTATION_RETRIES) {
                console.warn(`[OfflineSync] Cuarentena para mutación fallida tras ${MAX_MUTATION_RETRIES} intentos:`, item.id);
                await quarantineMutation(item, errorMessage);
            } else {
                await updateMutation({
                    ...item,
                    retries: currentRetries,
                    lastError: errorMessage
                });
            }
            failed++;
        }
    }

    bustDashboardStatsCache();
    queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });

    return { processed, failed };
}

async function runMutation(item: QueuedMutation): Promise<void> {
    const { type, payload } = item;

    switch (type) {
        case 'createOrder': {
            const order = payload.order as WorkOrder;
            if (order) await OrderService.createOrder(order);
            break;
        }
        case 'updateOrder': {
            const order = payload.order as WorkOrder;
            if (order) await OrderService.updateOrder(order);
            break;
        }
        case 'deleteOrder': {
            const orderId = payload.orderId as string;
            if (orderId) await OrderService.deleteOrder(orderId);
            break;
        }
        case 'deleteOrders': {
            const orderIds = payload.orderIds as string[];
            if (orderIds?.length) await Promise.all(orderIds.map((id) => OrderService.deleteOrder(id)));
            break;
        }
        case 'deleteEvent': {
            const eventId = payload.eventId as string;
            if (eventId) await OrderService.deleteEvent(eventId);
            break;
        }
        case 'updateEvent': {
            const eventId = payload.eventId as string;
            const newText = payload.newText as string;
            if (eventId != null && newText != null) await OrderService.updateEvent(eventId, newText);
            break;
        }
        default:
            console.warn('[OfflineSync] Unknown mutation type:', (item as QueuedMutation).type);
    }
}
